import React from "react";
import "./WhatIs.css"; // Import the CSS file for styling
import "../App.css"; // Import the CSS file for styling
import "@clr/icons/clr-icons.min.css";
import { ClarityIcons, atomIcon } from "@cds/core/icon";

// Define the WhatIs component
ClarityIcons.addIcons(atomIcon);

const WhatIs = () => {
  return (
    <div className="text-page">
      <header className="header">
        <h1 className="SubTitle">What is LESTO?</h1>
      </header>
      <main className="content">
        {/* About Section */}
        <section className="text-section">
          <h2>About LESTO</h2>
          <p className="WhatIs_list">
            The EU-funded LESTO project (LEad fast reactor Safety design and
            TOols) aims to further develop LFR technology, advancing it along
            its established roadmap and demonstrating its safety, efficiency,
            integration and reliability by employing the most relevant
            experimental facilities and providing a validation platform for
            numerical simulation tools.
          </p>
          <p className="WhatIs_list">
            Lead Fast Reactors (LFRs) are a crucial technology for advancing
            both hydrogen energy and nuclear power solutions, offering a
            promising and ambitious approach. Investors are already drawn to the
            advantages of competitive small- to medium-sized LFRs and their
            potential impact on the global market.
          </p>
        </section>

        {/* Project Details */}
        <section className="text-section">
          <h2>Project Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>Programme:</strong> Horizon Europe – EURATOM 2027: Safety
              of advanced and innovative nuclear designs
            </div>
            <div className="info-item">
              <strong>Grant Agreement:</strong> No 101166337
            </div>
            <div className="info-item">
              <strong>Duration:</strong> 4 years (until October 2028)
            </div>
            <div className="info-item">
              <strong>EU Contribution:</strong> ~ 4 M€
            </div>
            <div className="info-item">
              <strong>Project Coordinator:</strong> ENEA (Main contact: Simone
              Gianfelici – simone.gianfelici@enea.it)
            </div>
            <div className="info-item">
              <strong>Label:</strong> SNETP
            </div>
          </div>
        </section>

        {/* Consortium */}
        <section className="text-section">
          <h2>Consortium</h2>
          <p className="WhatIs_list">
            The LESTO project brings together recognized expertise from key
            universities, research centers and industrial partners across Europe
            and the UK to support the integration and further development of LFR
            technology.
          </p>
        </section>

        {/* Objectives */}
        <section className="text-section">
          <h2>Project Objectives</h2>
          <p className="WhatIs_list">
            To support the integration and the further development of LFR
            technology, in synergy with other LFR EU projects (HARMONISE,
            PATRICIA, ANSELMUS, PASCAL, INNUMAT, PUMMA), focusing on safety
            features and passive safety systems, LESTO addresses the following
            key topics:
          </p>
          <ul className="objectives-list">
            <li>
              Building up of a representative database for LFR pool
              thermal-hydraulic for V&V of numerical approaches (STH, CFD,
              multiscale approach) for large pools both in steady state and
              transient conditions
            </li>
            <li>
              Corrosion protection in Pb at high temperatures (up to 650°C),
              flow-induced erosion and fretting phenomena
            </li>
            <li>
              Coolant purification, oxygen detection and control for a pool-type
              configuration, release and transport of fission products
            </li>
            <li>Post-accident long-term management and decay heat removal</li>
            <li>
              Integration of LFR technology with renewable energy sources (RES)
              and life cycle of LFR reactor fuels
            </li>
          </ul>
        </section>

        {/* Work Packages */}
        <section className="text-section">
          <h2>Work Packages</h2>

          <div className="wp-section shadow-lg">
            <h3>WP1: LFR Sustainability</h3>
            <p>
              <strong>Goals:</strong>
            </p>
            <ul>
              <li>
                Combining the knowledge with the most promising LFR plant
                architectures to ensure that the level of safety is compliant
                with European standards
              </li>
              <li>
                Defining a robust and scalable plant architecture to be
                integrated with renewable energy sources (RES)
              </li>
              <li>
                Characterizing systematically the life cycle of LFR reactor
                fuels
              </li>
            </ul>
            <p>
              <strong>Addressed topics:</strong>
            </p>
            <ul>
              <li>
                Safety and Security of the Fuel Cycle: fuel availability,
                transport, different fuel types deployment, fuel performance and
                backend
              </li>
              <li>
                Integrability and Safety: investigate non-electric use of LFR
                technology and integration with RES
              </li>
            </ul>
          </div>

          <div className="wp-section shadow-lg">
            <h3>WP2: Passive Systems</h3>
            <p>
              <strong>Goals:</strong>
            </p>
            <ul>
              <li>
                To design and test inherently safe and passive systems to
                prevent or mitigate accidental scenarios
              </li>
              <li>
                To provide experimental data to support the validation of the
                numerical tools
              </li>
            </ul>
            <p>
              <strong>Addressed topics:</strong>
            </p>
            <ul>
              <li>
                In-vessel long-term accident management: the SIRIO facility,
                used in PIACE to study the effect of non-condensable gas
                injection in various reactor DHRs, will be modified to improve
                the quality of the data
              </li>
              <li>
                Ex-vessel long-term accident management: HERMES facility will
                analyze several RVACS water injection concepts, such as water
                jet injection, water spray, and water atomizer
              </li>
              <li>
                Post-test and V&V analyses: data collected will be used for V&V
                purposes
              </li>
            </ul>
          </div>

          <div className="wp-section shadow-lg">
            <h3>WP3: LFR Materials and Coolant Chemistry Control</h3>
            <p>
              <strong>Goals:</strong>
            </p>
            <p>
              Technical feasibility of the LFR systems, status of the components
              in liquid metal environment and the control of the coolant
              conditions during the plant operation
            </p>
            <p>
              <strong>Addressed topics:</strong>
            </p>
            <ul>
              <li>
                Coolant Chemistry Control and Purification: Validate the
                reliability of the oxygen detection and control systems in a
                large-scale, pool-type lead facility and test coolant
                purification systems for a pool-type LFR in the ATHENA facility
              </li>
              <li>
                LFR materials in lead at high temperatures: Validate the
                methodology for corrosion protection in lead at high
                temperatures; investigate oxidation kinetics, flow-induced
                erosion and fretting phenomena
              </li>
              <li>
                Numerical modelling of fission products release from coolant:
                Develop numerical models to predict release and transport of
                fission products
              </li>
            </ul>
          </div>

          <div className="wp-section shadow-lg">
            <h3>WP4: Thermal-Hydraulics of Large-Scale Systems</h3>
            <p>
              <strong>Goals:</strong>
            </p>
            <p>
              To deploy and validate full-scale models in CFD and system
              thermal-hydraulics codes in large facilities
            </p>
            <p>
              <strong>Addressed topics:</strong>
            </p>
            <ul>
              <li>
                Numerical tools Assessment for LFRs: Perform a deep assessment
                of the available computational tools dedicated to the
                thermal-hydraulics aspects of the LFRs and identify possible
                gaps
              </li>
              <li>
                Pre-test and post-test analysis of large-scale SGTR events:
                Characterize relevant phenomena for SGTR events in the CIRCE
                facility and provide data for V&V purposes
              </li>
              <li>
                Pre-test and post-test analysis of large-scale pool systems:
                real-size environment for testing of essential components in the
                ATHENA facility. Analyses will include both steady-state and
                transient experiments and will provide data for V&V purposes
              </li>
            </ul>
          </div>
        </section>

        {/* Education and Training */}
        <section className="text-section">
          <h2>Education, Training, Dissemination, Exploitation</h2>
          <p className="WhatIs_list">
            Active actions to maximize the impact and provide data for future
            applications, aiming at a long-lasting effect of the project:
          </p>
          <ul className="objectives-list">
            <li>
              <strong>Summer schools</strong> – Training the next generation of
              nuclear engineers
            </li>
            <li>
              <strong>Webinars</strong> – Regular knowledge sharing sessions
            </li>
            <li>
              <strong>Workshops</strong> – Collaborative events for stakeholders
            </li>
            <li>
              <strong>LFR simulation tools initiative</strong> – Open platform
              for open sharing of the status of numerical tools and their V&V
              for LFR applications
            </li>
          </ul>
        </section>

        {/* Expected Outcomes */}
        <section className="text-section">
          <h2>Expected Outcomes and Beyond LESTO</h2>
          <ul className="objectives-list">
            <li>Integration of LFR technology in energy mix including RES</li>
            <li>Viability: materials compatibility and the coolant control</li>
            <li>
              Prove mitigation/prevention of accidental scenarios with in- and
              ex-vessel passive systems
            </li>
            <li>
              Provide a European platform of experimental facilities capable to
              support the industry and the regulators in the deployment of the
              LFR technology
            </li>
            <li>
              Provide a European platform of simulation tools and start
              systematic validation actions to enhance the confidence in their
              results
            </li>
            <li>
              Push the interest in full-scale experiments for
              technology/simulation tools proofing
            </li>
            <li>
              Increase the interest in the public and possible investor in LFR
              technology
            </li>
          </ul>
        </section>

        {/* Funding Acknowledgment */}
        <section
          className="text-section"
          style={{
            marginTop: "2rem",
            padding: "1rem",
            backgroundColor: "#f0f4ff",
            borderRadius: "8px",
          }}
        >
          <p style={{ fontSize: "0.9rem", color: "#555", textAlign: "center" }}>
            This project has received funding from Horizon Europe – Euratom
            programme under grant agreement No 101166337.
          </p>
        </section>
      </main>
    </div>
  );
};

export default WhatIs;
